import { useConnectivityFlashItem } from '@etiquetachef/ui-shared/online';
import * as React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import AppLayout from '@cloudscape-design/components/app-layout';
import Flashbar from '@cloudscape-design/components/flashbar';
import SideNavigation, {
  type SideNavigationProps,
} from '@cloudscape-design/components/side-navigation';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import { useAuth } from '@/auth/AuthProvider';
import { useNotificationItems } from '@/lib/notifications';
import { AlertCountBadge } from '@/features/dashboard-alerts/menu-badge/AlertCountBadge';
import { NavVersionFooter } from '@/components/NavVersionFooter';

type NavItem = {
  to: string;
  labelKey: string;
  badge?: 'alerts';
  // `end`: rota é prefixo de outra; ativa só na correspondência exata.
  end?: boolean;
};
type NavGroup = { labelKey: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'admin.nav.tenants',
    items: [
      { to: '/tenants', labelKey: 'tenants.title' },
      // `/tenants` não é prefixo de `/users`, então `resolveActiveHref` já
      // distingue os dois sem precisar de `end`.
      { to: '/users', labelKey: 'adminPlatformUsers.title' },
    ],
  },
  {
    labelKey: 'admin.nav.billing',
    items: [
      { to: '/billing/pending', labelKey: 'billing.nav.pending' },
      // #78 — recebíveis de assinaturas (calendário semanal previsto × realizado).
      { to: '/billing/subscriptions', labelKey: 'billing.nav.subscriptions' },
      { to: '/billing/invoices', labelKey: 'billing.nav.invoices' },
      { to: '/billing/payments', labelKey: 'billing.nav.payments' },
      { to: '/billing/emails', labelKey: 'billing.nav.emails' },
      { to: '/billing/addons', labelKey: 'billing.nav.addons' },
      // Só as isenções desde a #679 F4 (cupons aposentados, 9.17).
      { to: '/billing/promotions', labelKey: 'promotions.tab.lifetime' },
      // #679 F7 — migração da cortesia/demo em etapas (E1–E4).
      { to: '/billing/courtesy-migration', labelKey: 'courtesyMigration.nav' },
      // Épico #72: itens transacionais do PSP (Stripe) movidos do antigo
      // grupo "Financeiro" para "Cobrança" — mesmo contexto (clientes).
      { to: '/finance/transactions', labelKey: 'admin.finance.nav.transactions' },
      { to: '/finance/refunds', labelKey: 'admin.finance.nav.refunds' },
      { to: '/finance/disputes', labelKey: 'admin.finance.nav.disputes' },
    ],
  },
  // Financeiro INTERNO da empresa (épico #72). Caixa próprio — contas
  // bancárias, origens/fontes, lançamentos (PRs seguintes). Não é billing.
  {
    labelKey: 'admin.nav.finance',
    items: [
      // Análise (PR D #76).
      { to: '/finance', labelKey: 'admin.finance.nav.overview' },
      { to: '/finance/cashflow', labelKey: 'admin.finance.nav.cashflow' },
      { to: '/finance/balance', labelKey: 'admin.finance.nav.balance' },
      // Movimento.
      { to: '/finance/entries', labelKey: 'admin.finance.nav.entries' },
      { to: '/finance/imports', labelKey: 'admin.finance.nav.imports' },
      // Cadastros.
      { to: '/finance/accounts', labelKey: 'admin.finance.nav.accounts' },
    ],
  },
  // BI de NEGÓCIO — métricas vindas do Firestore (billing/audit/tenants),
  // pré-agregadas pelos scheduled jobs. Estado interno é a fonte da verdade.
  {
    labelKey: 'admin.nav.business',
    items: [
      { to: '/dashboard/finance', labelKey: 'finance.menuLabel' },
      { to: '/dashboard/sales', labelKey: 'sales.menuLabel' },
      { to: '/dashboard/usage-basic', labelKey: 'usageBasic.menuLabel' },
      { to: '/dashboard/cohorts', labelKey: 'cohorts.menuLabel' },
      { to: '/dashboard/churn', labelKey: 'churn.menuLabel' },
      { to: '/dashboard/health', labelKey: 'health.menuLabel' },
      { to: '/dashboard/customers', labelKey: 'customers.menuLabel' },
      { to: '/dashboard/usage', labelKey: 'usage.menuLabel' },
      { to: '/dashboard/alerts', labelKey: 'alerts.menuLabel', badge: 'alerts' },
      { to: '/reports', labelKey: 'reports.menuLabel' },
    ],
  },
  // BI de PRODUTO — engajamento/uso vindo do PostHog (Product, Funnel,
  // Operação) + "Uso do admin" (telemetria do próprio admin). Inclui os
  // deep-links "Abrir no PostHog" nos cards.
  {
    labelKey: 'admin.nav.product',
    items: [
      { to: '/dashboard/product', labelKey: 'product.menuLabel' },
      { to: '/dashboard/funnel', labelKey: 'funnel.menuLabel' },
      { to: '/dashboard/operation', labelKey: 'operation.menuLabel' },
      { to: '/dashboard/admin-usage', labelKey: 'adminUsage.menuLabel' },
    ],
  },
  {
    labelKey: 'contact.nav.group',
    items: [
      { to: '/contact/messages', labelKey: 'contact.nav.messages' },
    ],
  },
  {
    labelKey: 'admin.nav.system',
    items: [
      { to: '/audit', labelKey: 'audit.title', end: true },
      { to: '/audit/billing', labelKey: 'auditBilling.menuLabel' },
      { to: '/notifications/broadcast', labelKey: 'notifications.broadcast.menuLabel' },
      { to: '/countries', labelKey: 'admin.countries.title' },
      // #935 (item 23, 2026-10-13): o mercado por país (PSP, modo, fiscal,
      // entidade) só se abria digitando a URL. Fica junto de Países e PSPs.
      { to: '/billing/markets', labelKey: 'billing.markets.title' },
      // #756 — PSPs: produção e sandbox, credenciais e teste de conexão.
      { to: '/psp', labelKey: 'admin.psp.title' },
      // #756 — as PJs que vendem e emitem nota (cada uma dona da conta de um
      // PSP). No menu Sistema, junto de PSPs, a pedido do owner (2026-10-01).
      { to: '/billing/seller-entities', labelKey: 'billing.sellers.title' },
      // `end`: /settings é prefixo de /settings/notifications e /super-admins.
      { to: '/settings', labelKey: 'admin.settings.title', end: true },
      { to: '/settings/notifications', labelKey: 'admin.notificationPrefs.navLabel' },
      { to: '/settings/super-admins', labelKey: 'superAdmins.title' },
      { to: '/settings/api-keys', labelKey: 'admin.apiKeys.navLabel' },
      { to: '/settings/app-versions', labelKey: 'admin.appVersionGate.navLabel' },
    ],
  },
];

// Lista achatada de itens com flag `end`, para resolver o href ativo a partir
// do pathname (Cloudscape destaca por correspondência exata de `activeHref`).
const FLAT_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/**
 * Resolve qual href de navegação deve aparecer como ativo. Em rotas aninhadas
 * (ex.: `/tenants/:id`) o item-pai (`/tenants`) continua ativo. Escolhe o
 * match mais longo; itens `end` exigem igualdade exata. Espelha o `isActive`
 * da sidebar shadcn anterior.
 */
function resolveActiveHref(pathname: string): string {
  let best = '';
  for (const item of FLAT_ITEMS) {
    const matches = item.end
      ? pathname === item.to
      : pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (matches && item.to.length > best.length) best = item.to;
  }
  return best;
}

const TOP_NAV_ID = 'admin-top-navigation';

/**
 * Shell autenticado do admin: barra superior (logo, e-mail, sair), menu
 * lateral agrupado (`NAV_GROUPS`), Flashbar de notificações e `<Outlet />`.
 *
 * Origem: sem issue — commit baefca8cc, 2026-04-26 (Sprint 2, lista de
 * tenants; o layout nasceu junto).
 *
 * Contrato: lê `useAuth()` (e-mail e `signOut`), `useNotificationItems()`
 * (fila de toasts do `@/lib/notifications`) e a rota atual. Clique em item do
 * menu vira `navigate()` do React Router (sem recarregar a página). Não lê
 * Firestore direto — o badge de alertas (`AlertCountBadge`) e o rodapé de
 * versão (`NavVersionFooter`) têm as próprias assinaturas.
 *
 * Usado por: `routes/router.tsx` — elemento da rota `/`, dentro do
 * `RequireSuperAdmin`; todas as telas autenticadas renderizam no `Outlet`.
 *
 * Arquitetura: só apresentação; a autorização é do `RequireSuperAdmin` (tela)
 * e do claim `superAdmin` nas rules/callables (dado). Rota nova sem item aqui
 * fica acessível só por link — inclua o item no grupo certo e, se o `to` for
 * prefixo de outra rota, marque `end`.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-13 (#935, item 23): `/billing/markets` não tinha item de
 *    menu (só se chegava pela URL); `/billing/overview/:tenantId`, que pede
 *    tenant, ganhou link no detalhe do tenant (`BillingActions`), não aqui.
 *
 * Dicas: a topbar do Cloudscape é sempre preta, por isso a logo é sempre a
 * variante noturna. `maxContentWidth={Number.MAX_VALUE}` tira o teto de
 * largura do `contentType="default"` (tabelas largas).
 *
 * Histórico:
 *  - 2026-10-25 14:29 +0100 — (#1021) `stickyNotifications` (Flashbar fixo no topo).
 *  - 2026-10-25 15:00 +0100 — (#1021) toast de erro antes do aviso de
 *    conectividade na pilha (`stackItems` só mostra o primeiro inteiro).
 */
export function AdminLayout(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, signOut } = useAuth();
  const email = status.kind === 'AUTHORIZED' ? (status.user.email ?? '') : '';
  // A topbar (Cloudscape TopNavigation) é SEMPRE preta, independente do
  // esquema de cores do SO. Por isso usamos sempre a logo noturna (com
  // "Etiqueta" em branco), que é a única legível em fundo escuro.
  const logoSrc = '/brand/logo-horizontal-dark.png';
  const notificationItems = useNotificationItems();
  // Web sempre online (#1010): aviso sem internet / hora do servidor pendente, no
  // Flashbar (topo do conteúdo, sob a barra superior) — depois dos erros (#1021, abaixo).
  const connectivityItem = useConnectivityFlashItem({
    offlineText: t('app.connectivity.offlineBanner'),
    clockPendingText: t('app.connectivity.clockPending'),
  });

  // Auditoria 2026-10-25 15:00 +0100 (#1021): com `stackItems` só o PRIMEIRO item aparece inteiro —
  // os outros viram um contador. O aviso de conectividade ia sempre primeiro, e offline o toast de erro
  // de uma ação ficava escondido na pilha. Erro vem antes; o aviso de conectividade volta ao topo
  // quando o toast some (8 s). Avisos que não são erro seguem depois dele.
  const flashItems = [
    ...notificationItems.filter((item) => item.type === 'error'),
    ...(connectivityItem === null ? [] : [connectivityItem]),
    ...notificationItems.filter((item) => item.type !== 'error'),
  ];

  const navItems = React.useMemo<readonly SideNavigationProps.Item[]>(
    () =>
      NAV_GROUPS.map((group) => ({
        type: 'section',
        text: t(group.labelKey),
        defaultExpanded: true,
        items: group.items.map((item) => ({
          type: 'link',
          text: t(item.labelKey),
          href: item.to,
          ...(item.badge === 'alerts' ? { info: <AlertCountBadge /> } : {}),
        })),
      })),
    [t],
  );

  const handleFollow = React.useCallback(
    (event: CustomEvent<{ href?: string; external?: boolean }>): void => {
      const href = event.detail.href;
      if (!href || event.detail.external) return;
      event.preventDefault();
      void navigate(href);
    },
    [navigate],
  );

  return (
    <>
      <div id={TOP_NAV_ID} style={{ position: 'sticky', top: 0, zIndex: 1002 }}>
        <TopNavigation
          identity={{
            href: '/',
            title: 'Admin',
            logo: { src: logoSrc, alt: 'EtiquetaChef' },
            onFollow: handleFollow,
          }}
          utilities={[
            {
              type: 'button',
              iconName: 'user-profile',
              text: email,
              disableUtilityCollapse: true,
            },
            {
              type: 'button',
              text: t('common.signOut'),
              ariaLabel: t('common.signOut'),
              onClick: (): void => {
                void signOut();
              },
            },
          ]}
          i18nStrings={{
            overflowMenuTriggerText: t('common.more', { defaultValue: 'Mais' }),
            overflowMenuTitleText: t('common.more', { defaultValue: 'Mais' }),
            overflowMenuBackIconAriaLabel: t('common.back', { defaultValue: 'Voltar' }),
            overflowMenuDismissIconAriaLabel: t('common.close', { defaultValue: 'Fechar' }),
          }}
        />
      </div>

      <AppLayout
        headerSelector={`#${TOP_NAV_ID}`}
        toolsHide
        contentType="default"
        // Remove o teto de largura do `contentType="default"` — o conteúdo
        // (tabelas, etc.) ocupa toda a largura disponível.
        maxContentWidth={Number.MAX_VALUE}
        // Auditoria 2026-10-25 14:29 +0100 (#1021): sem `stickyNotifications` o Flashbar rolava junto
        // com a página, e o toast de erro de quem clicou num botão do fim de uma página longa nascia
        // fora da tela (#756 já tinha esbarrado nisso). Fixo no topo, ele é visto onde o usuário
        // estiver; o overlay do Modal ainda o cobre, então erro de diálogo vai dentro do modal.
        stickyNotifications
        notifications={
          <Flashbar
            items={flashItems}
            stackItems
          />
        }
        navigation={
          // Coluna que ocupa a altura do painel: menu em cima, versão
          // colada embaixo (`marginTop: auto`).
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100%',
            }}
          >
            <SideNavigation
              activeHref={resolveActiveHref(location.pathname)}
              items={navItems}
              onFollow={handleFollow}
            />
            <div style={{ marginTop: 'auto' }}>
              <NavVersionFooter />
            </div>
          </div>
        }
        ariaLabels={{
          navigation: t('admin.nav.tenants'),
          navigationToggle: t('common.openNav', { defaultValue: 'Abrir navegação' }),
          navigationClose: t('common.closeNav', { defaultValue: 'Fechar navegação' }),
        }}
        content={<Outlet />}
      />
    </>
  );
}
